<script setup lang="ts">
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import Modal from '@/components/modal/Modal.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import SelectInput from '@/corePagesModule/components/SelectInput.vue';
import { i18n } from '@/i18n';
import { useExportOrderModalStore } from './useExportOrderModalStore';

const props = withDefaults(
  defineProps<{
    modalState?: boolean;
  }>(),
  {
    modalState: false,
  }
);

const emits = defineEmits<{
  (e: 'closeModal'): void;
  (e: 'exportOrder', file?: File): void;
}>();

const t = i18n.t;
const store = useExportOrderModalStore();
const { state } = storeToRefs(store);
const { fetchTemplates, setSelectedFormat, setSelectedTemplateId, setSelectedFile } = store;

const templateOptions = computed(() => state.value.templates.map((template) => template.fileOriginalName));

const selectedTemplateLabel = computed(() => {
  const template = state.value.templates.find((item) => item.templateId === state.value.selectedTemplateId);
  return template?.fileOriginalName ?? '';
});

watch(
  () => props.modalState,
  async (isOpen) => {
    if (!isOpen) {
      store.$reset();
      return;
    }
    await fetchTemplates();
  }
);

const isCustomDisabled = computed(() => {
  if (state.value.loading) {
    return true;
  }
  return state.value.templates.length === 0;
});

const showTemplateSelector = computed(() => state.value.selectedFormat === 'custom');
const showFileSelector = computed(() => state.value.selectedFormat === 'file');

const isExportDisabled = computed(() => {
  if (state.value.loading) {
    return true;
  }
  if (state.value.selectedFormat === 'custom') {
    return state.value.selectedTemplateId === '';
  }
  if (state.value.selectedFormat === 'file') {
    return state.value.selectedFile === null;
  }
  return false;
});

/**
 * 書式種別の選択変更を処理する
 * @param format - 選択された書式種別
 */
const handleFormatChange = (format: 'default' | 'custom' | 'file') => {
  setSelectedFormat(format);
};

/**
 * テンプレート選択変更を処理する
 * @param label - 選択されたテンプレート名
 */
const handleTemplateSelect = (label: string) => {
  const template = state.value.templates.find((item) => item.fileOriginalName === label);
  setSelectedTemplateId(template?.templateId ?? '');
};

/**
 * ファイル選択ボタン押下
 */
const onClickSelectFileButton = () => {
  document.getElementById('export-order-file-input')?.click();
};

/**
 * ファイル選択変更を処理する
 * @param event - ファイル選択イベント
 */
const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const file = target.files?.[0] ?? null;
  setSelectedFile(file);
};
</script>

<template>
  <Modal
    class="export-order-modal"
    :modal-size="400"
    :stretch-container="true"
    :scroll-off="true"
    :padding="'24px 32px'"
    :modal-state="modalState"
    :border-radius="'16px'"
    @outside-click="emits('closeModal')"
  >
    <div class="export-order-modal__content-container">
      <div class="export-order-modal__header">
        {{ t('発注書の出力') }}
      </div>

      <div class="export-order-modal__body body">
        <div class="body__section">
          <p class="body__section-title">{{ t('ダウンロードする書式') }}</p>
          <div class="body__format-options">
            <label class="body__format-option" for="export-format-default">
              <input
                id="export-format-default"
                class="body__radio"
                type="radio"
                name="export-format"
                :checked="state.selectedFormat === 'default'"
                @change="handleFormatChange('default')"
              />
              <span class="body__format-label">{{ t('デフォルト書式') }}</span>
            </label>
            <label class="body__format-option" for="export-format-custom">
              <input
                id="export-format-custom"
                class="body__radio"
                type="radio"
                name="export-format"
                :checked="state.selectedFormat === 'custom'"
                :disabled="isCustomDisabled"
                @change="handleFormatChange('custom')"
              />
              <span class="body__format-label">{{ t('ユーザー設定書式') }}</span>
            </label>
            <label class="body__format-option" for="export-format-file">
              <input
                id="export-format-file"
                class="body__radio"
                type="radio"
                name="export-format"
                :checked="state.selectedFormat === 'file'"
                @change="handleFormatChange('file')"
              />
              <span class="body__format-label">{{ t('出力書式選択') }}</span>
            </label>
          </div>
          <p v-if="state.loading" class="body__loading">{{ t('テンプレートを取得しています…') }}</p>
          <p v-if="state.errorMessage && !showTemplateSelector" class="body__error">
            {{ state.errorMessage }}
          </p>
        </div>

        <div v-if="showTemplateSelector" class="body__template template-selector">
          <p class="body__section-title template-selector__label">
            {{ t('ダウンロードする書式を選択してください') }}
          </p>
          <SelectInput
            class="template-selector__select"
            :index="0"
            :model-value="selectedTemplateLabel"
            :options="templateOptions"
            :placeholder="t('出力書式を選択')"
            :label="''"
            :disabled="state.loading"
            :is-error="!!state.errorMessage"
            :error-message="state.errorMessage"
            @update:model-value="handleTemplateSelect"
          />
        </div>

        <div v-if="showFileSelector" class="body__template file-selector">
          <p class="body__section-title file-selector__label">
            {{ t('ダウンロードする書式を選択してください') }}
          </p>
          <TextButton :button-style="'btn-primary'" :size="'btn-md'" :on-click="onClickSelectFileButton" class="file-selector__button">
            {{ t('出力ファイル+') }}
          </TextButton>
          <input
            id="export-order-file-input"
            class="file-selector__file-input"
            type="file"
            accept=".xlsx,.xls"
            @change="handleFileSelect"
          />
          <p v-if="state.selectedFile" class="file-selector__file-name">
            {{ state.selectedFile.name }}
          </p>
        </div>
      </div>

      <div class="export-order-modal__footer footer">
        <TextButton class="footer__button footer__button--cancel" @click="() => emits('closeModal')">
          {{ t('もどる') }}
        </TextButton>
        <TextButton
          class="footer__button footer__button--confirm"
          :disabled="isExportDisabled"
          @click="emits('exportOrder', state.selectedFile ?? undefined)"
        >
          {{ t('出力') }}
        </TextButton>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.export-order-modal {
  &__content-container {
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    gap: 24px;
  }

  &__header {
    color: $textDefault;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    width: 100%;
    text-align: center;
  }

  &__footer {
    display: grid;
    grid-template-columns: repeat(2, fit-content(50%));
  }
}

.body {
  display: grid;
  gap: 20px;

  &__section {
    display: grid;
    gap: 12px;
  }

  &__section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__format-options {
    display: grid;
    grid-template-columns: 1fr;
    gap: 8px;
  }

  &__format-option {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: 8px;
  }

  &__radio {
    width: 16px;
    height: 16px;
  }

  &__format-label {
    font-size: 14px;
    color: $textDefault;
  }

  &__loading {
    margin: 0;
    font-size: 13px;
    color: $textSecondary;
  }

  &__error {
    margin: 0;
    font-size: 13px;
    color: $semanticError;
  }
}

.template-selector {
  display: grid;
  gap: 16px;

  &__select {
    width: 100%;
  }
}

.file-selector {
  display: grid;
  gap: 16px;

  &__button {
    width: 160px;
    height: 40px;
  }

  &__file-input {
    display: none;
  }

  &__file-name {
    margin: 0;
    font-size: 14px;
    color: $textDefault;
    padding-left: 16px;
  }
}

.footer {
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-self: center;
  gap: 10px;

  &__button {
    width: 160px;
    height: 40px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;

    &--cancel {
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
      color: $semanticPrimary;
    }

    &--confirm {
      background-color: $semanticPrimary;
      color: $semanticWhite;
    }
  }
}
</style>
